<template>
  <div class="questionQueContainer">
    <div
      :class="[
        'progressContainer',
        { active: active },
        { hidden: progressHidden },
        { avatarsTurn: isAvatarsTurn }
      ]"
    >
      <ProgressBar :progress="progress" :gradient="progressGradient" height="20px" />
    </div>
    <div
      v-show="isRecording || isDemo"
      :class="[
        'indicatorOuterContainer',
        { active: active && (isDemo || isRecording || isAvatarsTurn) },
        { centered: isCentered },
        { avatarsTurn: isAvatarsTurn }
      ]"
    >
      <Transition name="fade">
        <div v-if="active && !isAvatarsTurn" class="recordingCueContainer">
          <div class="inner-circle">
            <FontAwesomeIcon :icon="faMicrophone" class="no-select" />
          </div>
        </div>
      </Transition>

      <Transition name="fade">
        <div v-if="!active || (isAvatarsTurn && active)" class="shush">
          <FontAwesomeIcon :icon="faTimes" size="5x" class="no-select" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, computed, watch } from 'vue'
import type { Ref, ComputedRef } from 'vue'

import ProgressBar from '@/components/ProgressBar.vue'

// Helpers
import { useTimeout } from '@/utils/helpers'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faMicrophone, faTimes } from '@fortawesome/free-solid-svg-icons'

const props = defineProps({
  waitTime: {
    type: Number,
    required: true
  },
  waitingForAnswer: {
    type: Boolean,
    required: true,
    default: false
  },
  isRecording: {
    type: Boolean,
    required: true
  },
  forceActive: {
    type: Boolean,
    required: false,
    default: false
  },
  isAvatarsTurn: {
    type: Boolean,
    default: false
  }
})

const { setManagedTimeout } = useTimeout()
const transitionDuration: number = 500
const cueDemoTime: number = 2000

const startTime: Ref<number> = ref(0)
const progress: Ref<number> = ref(0)
const interval: Ref<ReturnType<typeof setInterval> | number> = ref(0)

// Demo properties
const isDemo: Ref<boolean> = ref(false)
const isCentered: Ref<boolean> = ref(false)
const progressHidden: Ref<boolean> = ref(false)

const target: ComputedRef<number> = computed(() => {
  return props.waitTime * 1000
})

const active: ComputedRef<boolean> = computed(() => {
  return props.waitingForAnswer || props.forceActive
})

const progressGradient: ComputedRef<string> = computed(() => {
  return props.isAvatarsTurn
    ? 'linear-gradient(270deg, #f05262, #f05262)'
    : 'linear-gradient(270deg, #7a71f9, #8a83f5)'
})

const updateProgress = () => {
  const currentTime = new Date().getTime()
  progress.value = ((currentTime - startTime.value) / target.value) * 100 + 2
  if (progress.value >= 100) {
    clearInterval(interval.value)
  }
}

watch(
  () => props.waitingForAnswer,
  (newVal) => {
    if (newVal) {
      start()
    } else {
      stop()
    }
  }
)

async function demoShush() {
  return new Promise((resolve) => {
    isDemo.value = true
    isCentered.value = true
    progressHidden.value = true

    setManagedTimeout(() => {
      isCentered.value = false
      progressHidden.value = false

      setManagedTimeout(() => {
        isDemo.value = false
        resolve(true)
      }, transitionDuration)
    }, cueDemoTime)
  })
}

async function demoRecording() {
  return new Promise((resolve) => {
    isDemo.value = true
    isCentered.value = true
    progressHidden.value = true

    setManagedTimeout(() => {
      isCentered.value = false
      progressHidden.value = false

      setManagedTimeout(() => {
        isDemo.value = false
        resolve(true)
      }, transitionDuration)
    }, cueDemoTime)
  })
}

function start() {
  startTime.value = new Date().getTime()
  interval.value = setInterval(updateProgress, 100)
}

function stop() {
  clearInterval(interval.value)
  progress.value = 0
}

onBeforeUnmount(() => {
  clearInterval(interval.value)
})

defineExpose({
  demoShush,
  demoRecording,
  start,
  stop
})
</script>

<style lang="scss">
.questionQueContainer {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: calc(var(--full-vw) * 0.6);
  height: calc(var(--full-vh) * 0.15);
  min-height: 100px;
  display: flex;
  align-items: center;

  .progressContainer {
    flex-grow: 1;
    border: 2px solid $danger;
    border-radius: 99px;
    display: flex;
    opacity: 0.4;
    overflow: hidden;

    &.active {
      opacity: 1;
      border: 3px solid $primary;

      &.avatarsTurn {
        border: 3px solid $danger;
      }
    }

    &.hidden {
      visibility: hidden;
    }
  }
}

.indicatorOuterContainer {
  position: absolute;
  left: 50%;
  background-color: $white;
  width: 100px;
  height: 100px;
  transform: translateX(-50%);
  border-radius: 999px;
  border: 6px solid $danger;
  transition: all 0.5s;

  &.active {
    border: 6px solid $primary;
    left: 102%;

    &.avatarsTurn {
      border: 6px solid $danger;
      left: 102%;
    }
  }

  .recordingCueContainer,
  .shush {
    width: 100%;
    height: 100%;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &.centered {
    left: 50%;
    transform: translate(-50%, calc((var(--full-vh) / 2 * -1))) scale(3);
  }

  .shush {
    color: $danger;
  }

  .recordingCueContainer {
    color: $primary;

    &::before {
      content: '';
      width: 100%;
      height: 100%;
      background-color: rgba(185, 154, 238, 0.8);
      border-radius: 50%;
      position: absolute;
      @include animation(pulse 2s infinite ease-out);
    }

    .inner-circle {
      width: 60%;
      height: 60%;
      background-color: white;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      z-index: 1;
    }

    .inner-circle svg {
      width: 50%;
      height: 50%;
      fill: #b99aee;
    }

    @include keyframes(pulse) {
      0% {
        transform: scale(0.5);
        opacity: 0.8;
      }
      30% {
        opacity: 0.8;
      }
      100% {
        transform: scale(1.2);
        opacity: 0;
      }
    }
  }

  .inner-circle-border {
    path {
      fill: $white;
    }
  }

  .fade-enter-active {
    transition: all 0.5s ease-out;
  }

  .fade-leave-active {
    transition: all 0.5s ease-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
